import React, { memo, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  PaperProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { closeDialog, selectFuseDialogState } from 'app/store/fuse/dialogSlice';

type Props = {
  title: ReactNode;
  children: ReactNode;
  disableBackgroundClick?: boolean;
  titleCentered?: boolean;
  onCloseHandler?: () => void;
  showCloseButton?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  PaperProps?: PaperProps;
};

const Common = ({
  title,
  children,
  disableBackgroundClick = false,
  onCloseHandler,
  showCloseButton = false,
  maxWidth = 'sm',
  fullWidth = true,
}: Props) => {
  const state = useSelector(selectFuseDialogState);
  const dispatch = useDispatch();
  const matchesMax = useMediaQuery('(max-width:1440px)');

  const closeModal = (
    _event: React.SyntheticEvent<Element, Event>,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    const closedByUserAction = reason === 'backdropClick' || reason === 'escapeKeyDown';
    if (disableBackgroundClick && closedByUserAction) return;
    dispatch(closeDialog());
    onCloseHandler?.();
  };

  return (
    <Dialog
      open={state}
      onClose={closeModal}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          maxHeight: matchesMax ? 'calc(100% - 24px)' : 'calc(100% - 64px)',
          margin: matchesMax ? '12px' : '32px',
          overflowX: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderColor: 'divider',
          opacity: 0.8,
          padding: matchesMax ? '8px 24px' : '16px 24px',
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        className='px-0'
        sx={{
          pt: 1,
          pb: matchesMax ? '1px' : '8px',
          px: 2,
        }}
      >
        {children}
      </DialogContent>
      {showCloseButton && (
        <DialogActions>
          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              dispatch(closeDialog());
              onCloseHandler?.();
            }}
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(Common);
